/**
 * DeviceConfigurationForm — the one form to rule device configuration.
 *
 * Fetches the schema-driven config for a relay controller from the odl-app
 * endpoint, then renders its sections (with a firmware hint for next-gen units).
 * Holds the Formik state and PATCHes changed sections back. Handles its own
 * loading skeleton, error, and empty ("nothing editable") states. Section/field
 * rendering lives in SchemaSection.
 *
 * Contains:
 * - DeviceConfigurationForm — connected form (default export)
 */
import { Alert, Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchDeviceConfiguration, patchDeviceConfiguration } from '@sophocles/api/deviceConfiguration';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { deviceConfigurationRequiresCommandLease } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import {
  buildDeviceConfigurationInitialValues,
  buildDeviceConfigurationPatchBody,
} from '@sophocles/views/sites/components/DeviceConfiguration/deviceConfigurationWrite';
import FormikDirtyReporter from '@sophocles/views/sites/components/DeviceConfiguration/FormikDirtyReporter';
import SchemaSection from '@sophocles/views/sites/components/DeviceConfiguration/SchemaSection';

function ConfigSkeleton() {
  return (
    <Box sx={{ maxWidth: 520 }}>
      <Skeleton variant="text" width={160} sx={{ fontSize: '1.25rem', mb: 1 }} />
      <Stack spacing={2}>
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={40} />
        ))}
      </Stack>
    </Box>
  );
}

export default function DeviceConfigurationForm({
  siteId,
  relayControllerId,
  embedded = false,
  hideSaveButton = false,
  cicHasRadio = null,
  formikRef,
  onDirtyChange,
  onCommandRequiredChange,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  useEffect(() => {
    if (!relayControllerId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError(false);
    setNonFieldMessages([]);
    fetchDeviceConfiguration(relayControllerId)
      .then(data => active && setConfig(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [relayControllerId]);

  if (loading) return <ConfigSkeleton />;
  if (error)
    return <Alert severity="error">{t('deviceConfig.loadError', 'Could not load device configuration.')}</Alert>;
  if (!config) return null;

  const sections = config.schema?.sections || [];
  if (sections.length === 0) {
    return <Alert severity="info">{t('deviceConfig.noneEditable', 'No configurable settings for this device.')}</Alert>;
  }

  const initialValues = buildDeviceConfigurationInitialValues(config);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    // Send only the changed sub-tree; never PATCH the `''` placeholders
    // buildDeviceConfigurationInitialValues seeds for backend-omitted fields.
    const body = buildDeviceConfigurationPatchBody(values, initialValues);
    const requiresCommand = deviceConfigurationRequiresCommandLease(config, body, { cicHasRadio });
    setNonFieldMessages([]);
    try {
      const outcome = await runWrite(() => patchDeviceConfiguration(relayControllerId, body), { requiresCommand });
      if (!outcome.executed) return;
      // enableReinitialize re-seeds the form from the new initialValues.
      setConfig(outcome.value);
      dispatch(addToast({ message: t('deviceConfig.saved', 'Configuration saved'), level: 'success' }));
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors,
          // SchemaField doesn't bind Formik errors, so every field-prefixed
          // detail downgrades to the banner rather than vanishing unread.
          knownFields: [],
          genericMessage: t('deviceConfig.saveError', 'Could not save configuration'),
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit} innerRef={formikRef}>
      {({ dirty, isSubmitting, submitForm, values }) => {
        const pendingBody = buildDeviceConfigurationPatchBody(values, initialValues);
        const saveRequiresCommand = deviceConfigurationRequiresCommandLease(config, pendingBody, { cicHasRadio });
        const content = (
          <>
            {onDirtyChange && (
              <FormikDirtyReporter
                onDirtyChange={onDirtyChange}
                commandRequired={saveRequiresCommand}
                onCommandRequiredChange={onCommandRequiredChange}
              />
            )}
            <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
            {config.unit_type === 'next_gen' && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                {t('deviceConfig.nextGenHint', 'These settings are defined by the unit firmware.')}
              </Typography>
            )}
            {sections.map(section => (
              <SchemaSection key={section.key} section={section} />
            ))}
            {!hideSaveButton && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  type={embedded ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!dirty || isSubmitting || (commandLocked && saveRequiresCommand)}
                  onClick={embedded ? submitForm : undefined}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                >
                  {t('actions.save', 'Save')}
                </Button>
              </Stack>
            )}
          </>
        );

        return embedded ? <Box>{content}</Box> : <Form>{content}</Form>;
      }}
    </Formik>
  );
}

DeviceConfigurationForm.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  relayControllerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  embedded: PropTypes.bool,
  hideSaveButton: PropTypes.bool,
  cicHasRadio: PropTypes.bool,
  formikRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onDirtyChange: PropTypes.func,
  onCommandRequiredChange: PropTypes.func,
};
